import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import FadeIn from 'react-fade-in';

import { styled } from '@mui/material/styles';
import { TableContainer, TableHead, Table, TableRow, TableCell, TableBody, Paper, Grid } from '@mui/material';
import PropTypes from 'prop-types';

import { dynamicWords } from '../../../translation/dynamicWords';
import { mapHealthDataTypeNumToStr } from '../../../utils/conversions';
import { formatDateToString, getValuesGenerateKeys } from '../../../utils/helpers';
import {
  getRegionSpecificVisualAcuityValues,
  getRegionSpecificHealthDataTypes,
} from '../../../utils/configurationHelpers';
import {
  DIABETES_THERAPY_TYPE_STR,
  IOP,
  CORRECTED_VISUAL_ACUITY_DECIMAL,
  UNCORRECTED_VISUAL_ACUITY_DECIMAL,
  UNCORRECTED_VISUAL_ACUITY,
  CORRECTED_VISUAL_ACUITY,
} from '../../../constants/medicalRecords';
import { ReactComponent } from '../../../mocks/svg';

const StyledTableCell = styled(TableCell)(({ theme, tableHeight }) => ({
  backgroundColor: theme.palette.background.default,
  position: 'sticky',
  top: '0',
  zIndex: '10',
  fontSize: '11pt',
}));

const HealthDataTable = (props) => {
  const { healthData, extraHeaderHeight, tableHeight } = props;

  const staticWords = useSelector((state) => state.handlingTranslation.words);
  const { t, i18n } = useTranslation();
  const { strings: visualAcuityStrings } = getRegionSpecificVisualAcuityValues();
  const healthDataTypes = getRegionSpecificHealthDataTypes();

  const filteredHealthData = healthData.filter((el) => Object.keys(healthDataTypes)?.includes(el?.[0]));

  const translateText = (text) => {
    // Check if the input is a number or an object
    // If so, return it directly without processing
    if (typeof text === 'number' || typeof text === 'object') return text;
    // Split the string by commas, trim each word, translate, and join back with commas
    return text
      .split(',')
      .map((word) => {
        const trimmedWord = word.trim();
        const key = getKeyFromValue(trimmedWord); // Get the translation key for the trimmed word
        return t(key); // Translate the key using the translation function
      })
      .join(', '); // Join the translated words back into a single comma-separated string
  };

  const getKeyFromValue = (value) => {
    // Check in the English object first
    const enKey = Object.keys(dynamicWords.en).find((key) => dynamicWords.en[key] === value);
    // If the value is not found in the English object, check in the German object
    if (enKey) {
      return enKey;
    }
    const deKey = Object.keys(dynamicWords.de).find((key) => dynamicWords.de[key] === value);
    // Return the German key if found, else return the value itself
    return deKey || value;
  };

  return (
    <FadeIn>
      <TableContainer component={Paper} sx={{ maxHeight: tableHeight }}>
        <Table stickyHeader aria-label="health data table" sx={{ minWidth: 300 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ zIndex: 5, paddingRight: 0 }}>{staticWords.Type}</TableCell>
              <TableCell align="center" sx={{ width: '20%', paddingRight: 0 }}>
                {staticWords.Right_Eye}
              </TableCell>
              <TableCell align="center" sx={{ width: '20%', paddingRight: 0 }}>
                {staticWords.Left_Eye}
              </TableCell>
              <TableCell align="center" sx={{ width: '20%', paddingRight: 0, paddingLeft: '4px' }}>
                {staticWords.Recorded_Date}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredHealthData.map((el, i) => {
              const healthDataTypeNum = parseInt(el[0]);
              const healthDataType = healthDataTypes?.[el[0]];
              const recordedDate = el[1]?.recorded_date && el[1].recorded_date?.slice(0, 10);
              const hasLeftValues =
                (el[1].values?.Left !== undefined && el[1].values?.Right !== undefined) ||
                healthDataTypeNum === UNCORRECTED_VISUAL_ACUITY ||
                healthDataTypeNum === CORRECTED_VISUAL_ACUITY ||
                healthDataTypeNum === UNCORRECTED_VISUAL_ACUITY_DECIMAL ||
                healthDataTypeNum === CORRECTED_VISUAL_ACUITY_DECIMAL;

              const renderValues = () => {
                if (hasLeftValues) {
                  const rightValue = el[1].values?.Right ?? '';
                  const leftValue = el[1].values?.Left ?? '';
                  return (
                    <>
                      <TableCell align="center">
                        {el[0] === '9'
                          ? rightValue === 0
                            ? '-'
                            : rightValue
                          : rightValue === '' || rightValue === '-'
                            ? '-'
                            : t(visualAcuityStrings?.[rightValue])}
                      </TableCell>
                      <TableCell align="center">
                        {el[0] === '9'
                          ? leftValue === 0
                            ? '-'
                            : leftValue
                          : leftValue === '' || leftValue === '-'
                            ? '-'
                            : t(visualAcuityStrings?.[leftValue])}
                      </TableCell>
                    </>
                  );
                } else {
                  return (
                    <TableCell align="center" colSpan={2}>
                      {el[0] === '4'
                        ? el[1].values === '-' || el[1].values === 0
                          ? '-'
                          : el[1].values.map((x) => `${t(DIABETES_THERAPY_TYPE_STR[x])}`).join(', ')
                        : el[1].values === 0 || el[1].values === '0/0'
                          ? '-'
                          : el[1].values}
                    </TableCell>
                  );
                }
              };

              const renderTableSubheader = () => {
                if (parseInt(el[0]) === IOP) {
                  return (
                    <>
                      <TableRow sx={{ height: extraHeaderHeight + 'px' }}>
                        <TableCell sx={{ background: (theme) => theme.palette.background.default }}></TableCell>
                        <StyledTableCell align="center" colSpan={2}>
                          {staticWords.Data}
                        </StyledTableCell>
                        <TableCell sx={{ background: (theme) => theme.palette.background.default }}></TableCell>
                      </TableRow>
                    </>
                  );
                }
                return null;
              };

              return (
                <React.Fragment key={i}>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {staticWords[getValuesGenerateKeys(healthDataType)]}
                    </TableCell>
                    {renderValues()}
                    <TableCell align="center">{formatDateToString(recordedDate)}</TableCell>
                  </TableRow>
                  {renderTableSubheader()}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </FadeIn>
  );
};

HealthDataTable.propTypes = {
  healthData: PropTypes.array.isRequired,
};

export default HealthDataTable;
